// src/utils/analyticsPrivacy.ts
//
// Personal data must never reach Google Analytics (prawmi-odwr). GA4 records
// the full page URL on every hit (page_location, and page_referrer for the
// page after it), so any address that carries an e-mail or a one-time token in
// its query hands both to Google:
//
// - /dane?kod=x&cartId=<e-mail>&token=<64 hex>  - the registration link mailed
//   by send-verification-email (and the older `cartId=<e-mail>?token=` shape);
// - /zespol?zaproszenie=<token>                  - the team invitation link
//   mailed by backend/src/teams/service.py.
//
// redactUrlForAnalytics() is the one filter every URL goes through before it
// is handed to gtag (see ga.ts: gaPageContext, trackPageView, track, loadGA and
// app/components/GoogleAnalytics.tsx). Everything else in the URL is kept
// byte for byte, so the referral code (`kod`), plan and UTM parameters still
// reach GA and existing pagePathPlusQueryString rows do not split.

/**
 * Query parameters whose value identifies a person or works as a credential.
 * Compared case-insensitively. Add a name here whenever a link starts
 * carrying one - this list is the whole policy.
 */
export const SENSITIVE_QUERY_PARAMS: readonly string[] = [
  'cartId', // e-mail on /dane (registration link)
  'token', // registration token on /dane
  'zaproszenie', // team invitation token on /zespol
  'email',
  'e-mail',
  'mail',
  'oobCode', // Firebase auth action links
  'apiKey',
  'secret',
  'password',
];

const SENSITIVE_KEYS = new Set(SENSITIVE_QUERY_PARAMS.map((key) => key.toLowerCase()));

/** Replacement for an e-mail found anywhere else in a URL or an event value. */
export const EMAIL_PLACEHOLDER = 'email-redacted';

// An e-mail address, raw or percent-encoded (`a%40b.pl`, `a%2Bx%40b.pl`).
// On percent-encoded text the local part can swallow encoded words in front of
// the address (`do%20jan%40b.pl`), so query values are decoded before matching
// (redactPair); only the path and the fragment are matched raw, where an
// address is rare and over-redacting is harmless. No lookbehind: older Safari
// cannot parse it, and this module loads on every page.
const EMAIL_PATTERN = /[A-Za-z0-9._%+-]+(?:@|%40)[A-Za-z0-9.-]+\.[A-Za-z]{2,}/gi;

/** Replaces every e-mail address in `text` with {@link EMAIL_PLACEHOLDER}. */
export function redactEmails(text: string): string {
  return text.replace(EMAIL_PATTERN, EMAIL_PLACEHOLDER);
}

const decodeComponent = (raw: string): string | null => {
  try {
    return decodeURIComponent(raw.replace(/\+/g, ' '));
  } catch {
    return null;
  }
};

/** True when the raw `key=value` pair must be dropped altogether. */
const isSensitivePair = (pair: string): boolean => {
  const eq = pair.indexOf('=');
  const rawKey = eq === -1 ? pair : pair.slice(0, eq);
  const key = decodeComponent(rawKey) ?? rawKey;
  return SENSITIVE_KEYS.has(key.trim().toLowerCase());
};

/** The raw pair with any e-mail in its value replaced; untouched pairs stay byte for byte. */
const redactPair = (pair: string): string => {
  const eq = pair.indexOf('=');
  if (eq === -1) return redactEmails(pair);
  const rawValue = pair.slice(eq + 1);
  const value = decodeComponent(rawValue);
  if (value === null) return `${pair.slice(0, eq)}=${redactEmails(rawValue)}`;
  const safe = redactEmails(value);
  return safe === value ? pair : `${pair.slice(0, eq)}=${encodeURIComponent(safe)}`;
};

/**
 * The URL as GA may see it: sensitive query parameters removed, any e-mail
 * left elsewhere (path, other values, fragment) replaced. Works on absolute
 * and relative URLs and on the empty string. A URL with nothing to remove is
 * returned unchanged, character for character.
 *
 * The old registration link glued the token onto the e-mail
 * (`cartId=a%40b.pl?token=...`); splitting on `&` keeps that whole tail inside
 * the `cartId` pair, so dropping the pair drops the token too.
 */
export function redactUrlForAnalytics(url: string | null | undefined): string {
  if (!url) return '';

  const hashAt = url.indexOf('#');
  const beforeHash = hashAt === -1 ? url : url.slice(0, hashAt);
  const hash = hashAt === -1 ? '' : url.slice(hashAt);
  const queryAt = beforeHash.indexOf('?');
  const base = queryAt === -1 ? beforeHash : beforeHash.slice(0, queryAt);
  const query = queryAt === -1 ? null : beforeHash.slice(queryAt + 1);

  let changed = false;
  let keptQuery = query;
  if (query !== null) {
    const pairs = query.split('&');
    const kept = pairs
      .filter((pair) => !isSensitivePair(pair))
      .map(redactPair);
    const rebuilt = kept.join('&');
    if (rebuilt !== query) {
      changed = true;
      keptQuery = rebuilt;
    }
  }

  const safeBase = redactEmails(base);
  const safeHash = redactEmails(hash);
  if (!changed && safeBase === base && safeHash === hash) return url;

  return `${safeBase}${keptQuery ? `?${keptQuery}` : ''}${safeHash}`;
}

/**
 * Event parameters with every string value passed through redactEmails().
 * The net under `track()`: UTM values stashed from a link and caller-supplied
 * values reach GA without an address in them, whatever a future caller sends.
 */
export function redactEventParams(params: Record<string, any>): Record<string, any> {
  const safe: Record<string, any> = {};
  for (const [key, value] of Object.entries(params)) {
    safe[key] = typeof value === 'string' ? redactEmails(value) : value;
  }
  return safe;
}
